/**
 * PROJEKT-KONFIGURATION
 * ======================
 * Das ist die einzige Datei, die du für ein neues Modell/Projekt anfasst.
 * script.js (das Fundament: Loader, Render-Loop, Annotation-Engine,
 * Occlusion-Check, Kamera-Setup) bleibt unverändert.
 */

export const config = {

    /**
     * Modell
     */
    model: {
        url: `${import.meta.env.BASE_URL}models/Windkraftanlage/windkraftanlage.glb`,
        scale: 1, // ggf. an deine Blender-Skalierung anpassen
        rotationNode: 'anim_gondel'
    },

    /**
     * Environment Map
     * Equirectangular HDRI-Datei für Beleuchtung und Hintergrund.
     */
    environment: {
        hdrPath: `${import.meta.env.BASE_URL}textures/HDRI/white_cliff_top_1k.hdr`,
        backgroundColor: '#464646',
        rotationY: 0.5, // HDRI-Drehung um die Y-Achse in Radiant, z.B. Math.PI * 0.5
        envMapIntensity: 1
    },

    /**
     * Sonnenlicht (Directional Light)
     */
    sunlight: {
        color: '#ffffff',
        intensity: 0,
        position: [0.25, 1, -2.25],
        rotation: [34, 45, 0] // Blender-Rotation in Grad: X, Y, Z
    },

    /**
     * Materialeinstellungen
     */
    materials: {
        normalMapScale: 0.25,
    },

    /**
     * Zusätzliche Licht-/Effekt-Parameter für die Ankerlichter
     */
    lightEffects: {
        bloom: {
            strength: 0.1,
            radius: 0.55,
            threshold: 0.3,
            enabled: true
        },
        anchorLights: {
            intensity: 6,
            distance: 1,
            color: '#db190f',
            decay: 2
        }
    },

    /**
     * Render/Performance
     */
    render: {
        maxFps: 60,
        idleMaxFps: 6,
        idleTimeoutMs: 60000,
        fullyPausedTimeoutMs: 60000,
        pixelRatioLimit: 1.5,
        idlePixelRatioLimit: 1.1,
        idleBloomStrength: 0.15,
        fullyIdlePixelRatio: 0.75,
        fullyIdleBloomStrength: 0
    },

    /**
     * Kamera — "Modelleisenbahn"-Blickwinkel
     * Alle Werte sind Faktoren relativ zur automatisch berechneten Modellgröße
     * (Bounding-Box-Diagonale/2), nicht in Metern. Dadurch funktioniert die
     * Kamera unverändert egal ob dein Modell 2m oder 200m groß ist.
     * Optional — bei Bedarf weglassen, dann greifen die Defaults in script.js.
     */
    camera: {
        elevationFactor: 0.9,   // wie hoch über der Modellmitte die Kamera startet
        horizontalFactor: 0.6,  // seitlicher Versatz für den Blickwinkel
        nearFactor: 0.002,      // near-Clipping relativ zur Modellgröße
        farFactor: 6,           // far-Clipping relativ zur Modellgröße
        minDistanceFactor: 0.15,
        maxDistanceFactor: 3.5,
        zoomSpeed: 2,         // Mausrad-Zoom-Geschwindigkeit für OrbitControls
        // Wie nah die Kamera beim Heranfahren an eine Annotation herangeht,
        // relativ zur Modellgröße (siehe flyToAnnotation() in script.js)
        focusDistanceFactor: 0.25
    },

    /**
     * Annotationen
     * anchorName referenziert die "ANCHOR_"-Empties aus Blender
     * (Präfix wird in extractAnchors() abgeschnitten).
     * icon ist optional (Default in script.js: "+"), falls du ein anderes
     * Symbol für einen bestimmten Punkt willst (z.B. Zahnrad für bewegliche Teile).
     */
    annotations: [
        {
            id: 'welle',
            anchorName: 'welle',
            title: 'Welle',
            description: 'Die Hauptwelle überträgt das Drehmoment vom Rotor auf das Getriebe.',
            focusDistance: 5 // Meter — Bauteil ist klein, feste Nahdistanz statt Modell-relativ
        },
        {
            id: 'getriebe',
            anchorName: 'getriebe',
            title: 'Getriebe',
            description: 'Das Getriebe übersetzt die langsame Rotordrehzahl auf die hohe Drehzahl der schnellen Welle.',
            focusDistance: 5
        },

        {
            id: 'schnellewelle',
            anchorName: 'schnellewelle',
            title: 'Schnelle Welle',
            description: 'Verbindet Getriebe und Generator, dreht sich deutlich schneller als die Hauptwelle.',
            focusDistance: 5
        },
        {
            id: 'generator',
            anchorName: 'generator',
            title: 'Generator',
            description: 'Wandelt die mechanische Rotationsenergie in elektrische Energie um.',
            focusDistance: 5
        },
        {
            id: 'bremse',
            anchorName: 'bremse',
            title: 'Bremse',
            description: 'Stoppt den Rotor bei Bedarf.',
            focusDistance: 5
        },
        {
            id: 'messeinheit',
            anchorName: 'messeinheit',
            title: 'Messgerät',
            description: 'Zeigt die aktuelle Windgeschwindigkeit und Richung an.',
            focusDistance: 5
        },

        {
            id: 'rotor',
            anchorName: 'rotor',
            mode: 'animation',
            title: 'Rotor',
            description: 'Startet bzw. stoppt die Rotorbewegung.',
            icon: '↻',
            animationClip: 'RotorSpin',
            animationLoop: true,
            focusDistance: 10
        },
        {
            id: 'nacelle',
            anchorName: 'nacelle',
            mode: 'animation',
            title: 'Maschinenhaus',
            description: 'Klick öffnet die Verkleidung und gibt den Blick auf den Innenraum frei.',
            icon: '⚙',
            animationClip: 'NacelleOpen',
            animationLoop: false
            // kein focusDistance -> nutzt den globalen, modellgroßen-relativen Default
        }
    ]
}
